import StatusBadge from '@/components/StatusBadge';
import RemoteFile from '@/helper/RemoteFile';
import React from 'react';
import { Status } from '../../Gamics/types';

const gamicHeaders = [
  'Image',
  'Title',
  'Date',
  'Category',
  'Engagement',
  'Downloads',
  'Wishlists',
  'Likes',
  'Shares',
  'Comments',
  'Status',
];

const campaignHeaders = [
  'Image',
  'Gamic Title',
  'Campaign Name',
  'Date Range',
  'Budget',
  'Spent',
  'CPE',
  'CPD',
  'Status',
];

export interface RecentRow {
  image_url: string;
  title: string;
  created_at: string;
  categories: string[];
  engagements: number;
  downloads: number;
  wishlists: number;
  likes: number;
  shares: number;
  comments: number;
  gamic_title: string;
  start_date: string;
  end_date: string;
  total_budget: number;
  spent: number;
  cpe: number;
  cpd: number;
  status: Status;
}

interface RecentMetricsTableProps {
  rows: RecentRow[];
  type: 'Gamic' | 'Campaign';
}

export default function RecentMetricsTable({
  rows,
  type,
}: RecentMetricsTableProps) {
  let headers = [];

  if (type === 'Gamic') {
    headers = gamicHeaders;
  } else if (type === 'Campaign') {
    headers = campaignHeaders;
  } else {
    console.error('Unknown type:', type);
    return null; // or handle error appropriately
  }

  interface FormatLocal {
    (isoString: string): string;
  }

  const formatLocal: FormatLocal = (isoString: string): string => {
    const d = new Date(isoString);
    const opts: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    return d.toLocaleDateString(undefined, opts);
  };

  return (
    <div className='overflow-x-auto bg-white'>
      {/* min-width stops wrapping; scrolls on X only */}
      <table className='min-w-[950px] whitespace-nowrap w-full text-left'>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h} className='table-title font-bold px-4 py-2'>
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className='border-y border-[#0000000D] border-l-0 border-r-0'
            >
              <td className='p-2'>
                <RemoteFile
                  className='h-[84px] w-[56px] object-cover rounded'
                  keyPath={r.image_url}
                  alt={r.title}
                />
              </td>

              {/* agar type “gamic” ho */}
              {type === 'Gamic' && (
                <>
                  <td className='p-4'>{r.title}</td>
                  <td className='p-4'>{r.created_at}</td>
                  <td className='p-4'>{r.categories.join(', ')}</td>
                  <td className='p-4'>{r.engagements}</td>
                  <td className='p-4'>{r.downloads}</td>
                  <td className='p-4'>{r.wishlists}</td>
                  <td className='p-4'>{r.likes}</td>
                  <td className='p-4'>{r.shares}</td>
                  <td className='p-4'>{r.comments}</td>
                  <td className='p-4'>
                    <StatusBadge status={r.status} />
                  </td>
                </>
              )}

              {/* agar type “campaign” ho */}
              {type === 'Campaign' && (
                <>
                  <td className='p-4'>{r.gamic_title}</td>
                  <td className='p-4'>{r.title}</td>
                  <td className='p-4'>
                    {`${formatLocal(r.start_date)} - ${formatLocal(r.end_date)}`}
                  </td>
                  <td className='p-4'>{'$ ' + r.total_budget}</td>
                  <td className='p-4'>{'$ ' + (r.spent ?? 0)}</td>
                  <td className='p-4'>{'$ ' + r.cpe}</td>
                  <td className='p-4'>{'$ ' + r.cpd}</td>
                  <td className='p-4'>
                    <StatusBadge status={r.status} />
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
